import React, { useState } from 'react';
import { format } from 'date-fns';
import PresetExpenseSelector from './PresetExpenseSelector';
import ScrollIndicator from '../ScrollIndicator';

const ExpensesSection = ({ expenses = [], onExpensesChange, isEditing = false, employeeLaborTotal = 0, isPublicView = false }) => {
  const [newExpense, setNewExpense] = useState({
    description: '',
    amount: '',
    date: format(new Date(), 'yyyy-MM-dd')
  });

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === '') return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(Number(amount));
  };

  const resetForm = () => {
    setNewExpense({
      description: '',
      amount: '',
      date: format(new Date(), 'yyyy-MM-dd')
    });
  };

  const handleAddExpense = () => {
    if (!newExpense.description.trim() || !newExpense.amount || !newExpense.date) {
      alert('Please fill in all expense fields');
      return;
    }

    const expense = {
      id: Date.now(), // Temporary ID for new expenses
      description: newExpense.description.trim(),
      amount: parseFloat(newExpense.amount),
      date: newExpense.date,
      isNew: true // Mark as new for saving
    };

    const updatedExpenses = [...expenses, expense];
    onExpensesChange(updatedExpenses);

    // Reset form after adding
    resetForm();
  };

  const handleRemoveExpense = (expenseId) => {
    if (window.confirm('Are you sure you want to remove this expense?')) {
      const updatedExpenses = expenses.filter(expense => expense.id !== expenseId);
      onExpensesChange(updatedExpenses);
    }
  };

  const handleAddPresetExpenses = (presetExpenses) => {
    const updatedExpenses = [...expenses, ...presetExpenses];
    onExpensesChange(updatedExpenses);
  };

  const handleEditExpense = (expenseId, field, value) => {
    const updatedExpenses = expenses.map(expense => {
      if (expense.id === expenseId) {
        return {
          ...expense,
          [field]: field === 'amount' ? parseFloat(value) || 0 : value,
          isEdited: true // Mark as edited for saving
        };
      }
      return expense;
    });
    onExpensesChange(updatedExpenses);
  };

  // Calculate employee tax (11% of employee labor)
  const employeeTax = employeeLaborTotal * 0.11;
  
  // Calculate manual expenses total
  const manualExpensesTotal = expenses.reduce((sum, expense) => sum + (Number(expense.amount) || 0), 0);
  
  // Total expenses includes manual expenses + employee tax
  const totalExpenses = manualExpensesTotal + employeeTax;

  return (
    <div className={`bg-white ${isPublicView ? 'lg:px-6' : ''}`}>
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Expenses</h3>
          <div className="text-sm text-gray-500">
            {expenses.length} {expenses.length === 1 ? 'expense' : 'expenses'}
          </div>
        </div>
      </div>

      {/* Always-Ready Add Expense Input (only when editing) */}
      {isEditing && (
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="flex items-end space-x-3">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Description
              </label>
              <input
                type="text"
                value={newExpense.description}
                onChange={(e) => setNewExpense({ ...newExpense, description: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                placeholder="Enter expense description"
              />
            </div>
            <div className="w-32">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Date
              </label>
              <input
                type="date"
                value={newExpense.date}
                onChange={(e) => setNewExpense({ ...newExpense, date: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
              />
            </div>
            <div className="w-24">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Amount
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-gray-500 text-sm">$</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newExpense.amount}
                  onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })}
                  className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                  placeholder="0.00"
                />
              </div>
            </div>
            <button
              onClick={handleAddExpense}
              disabled={!newExpense.description.trim() || !newExpense.amount || !newExpense.date}
              className="px-3 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Add Expense"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
            </button>
          </div>
          
          {/* Preset Expenses Selector */}
          <div className="mt-4">
            <PresetExpenseSelector onAddPresetExpenses={handleAddPresetExpenses} />
          </div>
        </div>
      )}

      {/* Existing Expenses List */}
      {expenses.length > 0 || employeeLaborTotal > 0 ? (
        <ScrollIndicator>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider text-right">
                  Amount
                </th>
                {isEditing && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {expenses.length === 0 && employeeLaborTotal > 0 && (
                <tr className="bg-gray-50">
                  <td colSpan={isEditing ? "4" : "3"} className="px-6 py-4 text-center text-sm text-gray-500 italic">
                    No manual expenses added
                  </td>
                </tr>
              )}
              {expenses.length > 0 && expenses.map((expense, index) => (
                <tr key={expense.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {isEditing ? (
                      <input
                        type="text"
                        value={expense.description}
                        onChange={(e) => handleEditExpense(expense.id, 'description', e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-sky-500"
                        placeholder="Expense description"
                      />
                    ) : (
                      <div className="font-medium">{expense.description}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {isEditing ? (
                      <input
                        type="date"
                        value={expense.date}
                        onChange={(e) => handleEditExpense(expense.id, 'date', e.target.value)}
                        className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-sky-500"
                      />
                    ) : (
                      format(new Date(expense.date), 'MMM dd, yyyy')
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                    {isEditing ? (
                      <div className="relative">
                        <span className="absolute left-2 top-1 text-gray-500 text-sm">$</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={expense.amount}
                          onChange={(e) => handleEditExpense(expense.id, 'amount', e.target.value)}
                          className="w-24 pl-6 pr-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-sky-500"
                          placeholder="0.00"
                        />
                      </div>
                    ) : (
                      <span className="font-medium">{formatCurrency(expense.amount)}</span>
                    )}
                  </td>
                  {isEditing && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <button
                        onClick={() => handleRemoveExpense(expense.id)}
                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500"
                      >
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Remove
                      </button>
                    </td>
                  )}
                </tr>
              ))}
              
              {/* Employee Tax Row - Always displayed, calculated automatically */}
              {employeeLaborTotal > 0 && (
                <tr className="bg-yellow-50 border-t-2 border-yellow-200">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    Employee Tax
                    <span className="ml-2 text-xs text-gray-500 font-normal">(11% of Employee Labor)</span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    Auto-calculated
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                    {formatCurrency(employeeTax)}
                  </td>
                  {isEditing && (
                    <td className="px-6 py-4"></td>
                  )}
                </tr>
              )}
            </tbody>
            <tfoot className="bg-blue-50 border-t-2 border-gray-300">
              <tr>
                <td colSpan="2" className="px-6 py-4 text-sm font-bold text-gray-900">
                  Total Expenses
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                  {formatCurrency(totalExpenses)}
                </td>
                {isEditing && (
                  <td className="px-6 py-4"></td>
                )}
              </tr>
            </tfoot>
          </table>
        </ScrollIndicator>
      ) : (
        /* Empty State */
        <div className="px-6 py-8 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2zM10 8.5a.5.5 0 11-1 0 .5.5 0 011 0zm5 5a.5.5 0 11-1 0 .5.5 0 011 0z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No expenses</h3>
          <p className="mt-1 text-sm text-gray-500">
            {isEditing ? 'Use the input field above to add your first expense.' : 'No expenses have been added to this report yet.'}
          </p>
        </div>
      )}
    </div>
  );
};

export default ExpensesSection; 